import { useState, useCallback, useMemo, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	BsChevronLeft,
	BsChevronRight,
	BsCalendar3,
	BsCalendarWeek,
	BsCalendarDay,
	BsX,
	BsExclamationTriangle,
	BsPlus,
	BsGoogle,
	BsMicrosoft,
} from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { STALE_TIMES } from '#lib/queryConstants';
import { usePreferencesStore } from '#lib/stores/preferencesStore';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import { fetchCalendarEvents, deleteCalendarEvent } from '../../api/crmApi';
import ActivityDrawer from '../../components/activities/ActivityDrawer/ActivityDrawer';
import CrmCalendar from '../../components/calendar/CrmCalendar/CrmCalendar';
import MeetingScheduleModal from '../../components/calendar/MeetingScheduleModal/MeetingScheduleModal';
import CrmSearchBar from '../../components/common/CrmSearchBar/CrmSearchBar';
import { USER_ACTIVITY_TYPES, getActivityType } from '../../constants/activityTypes';
import { useCompleteActivity, useUpdateActivity, useDeleteActivity } from '../../models/activities/mutations';
import { useActivitiesList } from '../../models/activities/queries';
import { useCalendarConnections } from '../../models/calendar/queries';
import { useChannelTypes } from '../../models/channels/queries';
import { resolveChannelIcon } from '../../utils/channelIcons';

const VIEW_OPTIONS = [
	{ key: 'dayGridMonth', label: 'Monat', icon: BsCalendar3 },
	{ key: 'timeGridWeek', label: 'Woche', icon: BsCalendarWeek },
	{ key: 'timeGridDay', label: 'Tag', icon: BsCalendarDay },
];

const CrmCalendarPage = () => {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const calendarRef = useRef(null);
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];
	const { data: calendarConnections = [] } = useCalendarConnections();
	const calendarVisibility = usePreferencesStore((s) => s.calendarVisibility);
	const setCalendarVisibility = usePreferencesStore((s) => s.setCalendarVisibility);
	const { data: channelTypes = [] } = useChannelTypes(false);
	const activeChannels = channelTypes.filter((c) => c.orgEnabled !== false);

	// View & navigation state
	const [currentView, setCurrentView] = useState('timeGridWeek');
	const [calendarTitle, setCalendarTitle] = useState('');
	const [dateRange, setDateRange] = useState({ start: null, end: null });

	// Filters
	const [typeFilters, setTypeFilters] = useState([]);
	const [channelFilters, setChannelFilters] = useState([]);

	// UI state
	const [openId, setOpenId] = useState(null);
	const [completingId, setCompletingId] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [meetingModal, setMeetingModal] = useState({ open: false, prefillDate: null });

	// Helpers
	const toggleType = (t) => {
		setTypeFilters((f) => (f.includes(t) ? f.filter((x) => x !== t) : [...f, t]));
		// Clear channel sub-filters when communication is deselected
		if (t === 'communication') setChannelFilters([]);
	};
	const toggleChannel = (id) => setChannelFilters((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
	const clearFilters = () => {
		setTypeFilters([]);
		setChannelFilters([]);
	};
	const activeFilterCount = (typeFilters.length > 0 ? 1 : 0) + (channelFilters.length > 0 ? 1 : 0);

	// Programmatic FullCalendar control
	const getApi = () => calendarRef.current?.getApi?.();
	const goPrev = () => getApi()?.prev();
	const goNext = () => getApi()?.next();
	const goToday = () => getApi()?.today();
	const changeView = (view) => {
		getApi()?.changeView(view);
		setCurrentView(view);
	};

	// Data: CRM activities (internal) + live-fetched external calendar events
	const rangeReady = Boolean(dateRange.start) && Boolean(dateRange.end);

	const queryFilters = useMemo(
		() => ({
			pageSize: 200,
			orderBy: 'dueAt',
			orderDir: 'asc',
			...(dateRange.start ? { dueAfter: dateRange.start } : {}),
			...(dateRange.end ? { dueBefore: dateRange.end } : {}),
		}),
		[dateRange],
	);

	const { data: activitiesData } = useActivitiesList(queryFilters, { enabled: rangeReady });
	const crmActivities = useMemo(() => activitiesData?.activities ?? [], [activitiesData?.activities]);

	// Live-fetch external calendar events (not stored in DB)
	const { data: calendarData } = useQuery({
		queryKey: ['crm', 'calendar', 'events', dateRange.start, dateRange.end],
		queryFn: () => fetchCalendarEvents({ start: dateRange.start, end: dateRange.end }),
		enabled: rangeReady,
		staleTime: STALE_TIMES.STANDARD,
	});
	const externalEvents = (calendarData?.events ?? []).map((e) => ({
		...e,
		activityType: 'meeting',
		status: new Date(e.dueAt) < new Date() ? 'completed' : 'planned',
		channelPayload: { durationMinutes: e.durationMinutes, meetingUrl: e.meetingUrl, attendees: e.attendees },
		source: 'external',
	}));

	const allActivities = useMemo(() => [...crmActivities, ...externalEvents], [crmActivities, externalEvents]);

	const completeMutation = useCompleteActivity();
	const updateMutation = useUpdateActivity();
	const deleteMutation = useDeleteActivity();

	// Client-side filtering
	const filtered = useMemo(
		() =>
			allActivities.filter((a) => {
				if (a.activityType === 'stage_change' || a.activityType === 'system') return false;
				if (typeFilters.length > 0 && !typeFilters.includes(a.activityType)) return false;
				if (
					channelFilters.length > 0 &&
					a.activityType === 'communication' &&
					!channelFilters.includes(a.channelTypeId)
				)
					return false;
				if (a.connectionId && calendarVisibility[a.connectionId] === false) return false;
				return true;
			}),
		[allActivities, typeFilters, channelFilters, calendarVisibility],
	);

	// Stats
	const stats = useMemo(() => {
		const open = allActivities.filter(
			(a) =>
				a.status !== 'completed' &&
				a.status !== 'cancelled' &&
				a.status !== 'failed' &&
				a.activityType !== 'stage_change' &&
				a.activityType !== 'system',
		);
		const now = new Date();
		const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
		const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
		return {
			overdue: open.filter((a) => a.dueAt && new Date(a.dueAt) < todayStart).length,
			today: open.filter((a) => a.dueAt && new Date(a.dueAt) >= todayStart && new Date(a.dueAt) < todayEnd)
				.length,
			week: open.filter((a) => a.dueAt && new Date(a.dueAt) >= todayStart && new Date(a.dueAt) < weekEnd).length,
			total: open.length,
		};
	}, [allActivities]);

	// Handlers
	const handleDatesSet = useCallback(({ start, end }) => {
		setDateRange({
			start: new Date(start).toISOString(),
			end: new Date(end).toISOString(),
		});
		const title = calendarRef.current?.getApi?.()?.view?.title ?? '';
		setCalendarTitle(title);
	}, []);

	const handleEventClick = useCallback((activity) => {
		setOpenId(activity.id);
	}, []);

	const handleDateClick = useCallback((dateStr) => {
		setMeetingModal({ open: true, prefillDate: dateStr });
	}, []);

	const handleComplete = async (e, activity) => {
		e.stopPropagation();
		if (completingId) return;
		setCompletingId(activity.id);
		try {
			if (activity.status === 'completed') {
				await updateMutation.mutateAsync({ id: activity.id, payload: { status: 'planned' } });
			} else {
				await completeMutation.mutateAsync({ id: activity.id, payload: {} });
			}
		} finally {
			setCompletingId(null);
		}
	};

	const handleDelete = (id) => {
		const activity = allActivities.find((a) => a.id === id);
		setDeleteTarget(activity ?? { id });
	};

	const confirmDelete = async () => {
		if (!deleteTarget) return;
		const targetId = deleteTarget.id;
		const isExternal = deleteTarget.source === 'external';

		// Optimistic: remove from UI immediately
		const calendarQueries = qc.getQueriesData({ queryKey: ['crm', 'calendar', 'events'] });
		if (isExternal) {
			for (const [key, data] of calendarQueries) {
				if (data?.events) {
					qc.setQueryData(key, {
						...data,
						events: data.events.filter((e) => e.id !== targetId.replace(/^(mscal_|gcal_)/, '')),
					});
				}
			}
		}

		if (openId === targetId) setOpenId(null);
		setDeleteTarget(null);

		try {
			if (isExternal) {
				const rawId = targetId.replace(/^(mscal_|gcal_)/, '');
				await deleteCalendarEvent({ connectionId: deleteTarget.connectionId, providerEventId: rawId });
			} else {
				await deleteMutation.mutateAsync(targetId);
			}
		} catch {
			// Rollback: restore calendar data
			for (const [key, data] of calendarQueries) {
				qc.setQueryData(key, data);
			}
		} finally {
			qc.invalidateQueries({ queryKey: ['crm', 'calendar', 'events'] });
		}
	};

	const handleReassign = async (id, userId) => {
		await updateMutation.mutateAsync({ id, payload: { userId } });
	};

	const openActivity = allActivities.find((a) => a.id === openId);

	return (
		<Page
			width="full"
			scrollMode="content"
			header={
				<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Kalender']}>
					<div className="w-64">
						<CrmSearchBar />
					</div>
					<Button
						variant="solid"
						size="sm"
						onClick={() => setMeetingModal({ open: true, prefillDate: null })}
					>
						<BsPlus className="w-4 h-4 -ml-0.5" /> Neues Meeting
					</Button>
				</PageHeader>
			}
		>
			<div className="flex flex-1 min-h-0">
				<aside className="w-60 flex-shrink-0 border-r border-bgBase-500 bg-white p-3.5 overflow-y-auto flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1 mb-1">
							Ansicht
						</span>
						{VIEW_OPTIONS.map((v) => {
							const Icon = v.icon;
							const active = currentView === v.key;
							return (
								<button
									key={v.key}
									type="button"
									onClick={() => changeView(v.key)}
									className={`flex items-center gap-2 w-full px-2.5 py-[7px] rounded-control text-[12.5px] font-medium text-left transition-colors ${
										active
											? 'bg-primary-100 text-primary-700 font-semibold'
											: 'text-bgBase-800 hover:bg-bgBase-300 hover:text-text'
									}`}
								>
									<Icon className="w-3.5 h-3.5" />
									{v.label}
								</button>
							);
						})}
					</div>

					<div className="flex flex-col gap-1.5">
						<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1">
							Aktivitätstyp
						</span>
						<div className="grid grid-cols-2 gap-1">
							{USER_ACTIVITY_TYPES.map((t) => {
								const active = typeFilters.includes(t.id);
								const Icon = t.icon;
								return (
									<button
										key={t.id}
										type="button"
										onClick={() => toggleType(t.id)}
										className={`inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium border rounded-control transition-colors ${
											active
												? 'font-semibold'
												: 'border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300'
										}`}
										style={
											active
												? { background: t.color + '1a', borderColor: t.color, color: t.color }
												: {}
										}
									>
										<Icon className="w-3 h-3" /> {t.label}
									</button>
								);
							})}
						</div>

						{/* Channel sub-filters (shown when communication is active) */}
						{typeFilters.includes('communication') && activeChannels.length > 0 && (
							<div className="flex flex-wrap gap-1 pl-1 pt-0.5">
								{activeChannels.map((ch) => {
									const active = channelFilters.includes(ch.id);
									const ChIcon = resolveChannelIcon(ch);
									return (
										<button
											key={ch.id}
											type="button"
											onClick={() => toggleChannel(ch.id)}
											className={`inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium border rounded-control transition-colors ${
												active
													? 'border-primary-300 bg-primary-100 text-primary-700 font-semibold'
													: 'border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300 hover:text-text'
											}`}
										>
											<ChIcon className="w-2.5 h-2.5" /> {ch.displayName}
										</button>
									);
								})}
							</div>
						)}
					</div>

					{calendarConnections.length > 0 && (
						<div className="flex flex-col gap-1.5">
							<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1">
								Kalender
							</span>
							{calendarConnections.map((conn) => {
								const ProviderIcon = conn.provider === 'google_calendar' ? BsGoogle : BsMicrosoft;
								const providerColor =
									conn.provider === 'google_calendar' ? 'text-warning-500' : 'text-blue-500';
								const visible = calendarVisibility[conn.id] !== false;
								return (
									<label
										key={conn.id}
										className="flex items-center gap-2 px-2 py-1.5 rounded-control text-[12px] cursor-pointer hover:bg-bgBase-300 transition-colors"
									>
										<Checkbox
											id={`calendar-visible-${conn.id}`}
											checked={visible}
											onChange={() => setCalendarVisibility(conn.id, !visible)}
										/>
										<ProviderIcon className={`w-3 h-3 flex-shrink-0 ${providerColor}`} />
										<span
											className={`truncate ${visible ? 'text-text font-medium' : 'text-bgBase-800'}`}
										>
											{conn.label || conn.metadata?.calendarEmail}
										</span>
									</label>
								);
							})}
						</div>
					)}
				</aside>

				<div className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6">
					<div className="flex items-center justify-between gap-4 mb-4">
						<div className="flex items-center gap-3">
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={goPrev}
									className="w-8 h-8 rounded-control border border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300 hover:text-text flex items-center justify-center transition-colors"
								>
									<BsChevronLeft className="w-3.5 h-3.5" />
								</button>
								<button
									type="button"
									onClick={goNext}
									className="w-8 h-8 rounded-control border border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300 hover:text-text flex items-center justify-center transition-colors"
								>
									<BsChevronRight className="w-3.5 h-3.5" />
								</button>
							</div>
							<Button onClick={goToday} variant="outline" size="sm">
								Heute
							</Button>
							<h2 className="text-[15px] font-bold text-text tracking-tight">{calendarTitle}</h2>
						</div>

						<div className="flex items-center gap-2 text-xs text-bgBase-800">
							{stats.overdue > 0 && (
								<>
									<span className="inline-flex items-center gap-1 font-semibold text-warning-500">
										<BsExclamationTriangle className="w-3 h-3" /> {stats.overdue}
									</span>
									<span className="text-bgBase-800">&middot;</span>
								</>
							)}
							<span>
								<span className="font-semibold text-text">{stats.today}</span> heute
							</span>
							<span className="text-bgBase-800">&middot;</span>
							<span>
								<span className="font-semibold text-text">{stats.week}</span> diese Woche
							</span>
							<span className="text-bgBase-800">&middot;</span>
							<span className="text-bgBase-800">{stats.total} offen</span>
						</div>
					</div>

					{activeFilterCount > 0 && (
						<div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-dashed border-bgBase-500">
							{typeFilters.map((t) => {
								const meta = getActivityType(t);
								return (
									<span
										key={t}
										className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-100 text-primary-700 border border-primary-200 rounded-full pl-2.5 pr-1 py-0.5"
									>
										{meta.label}
										<button
											type="button"
											onClick={() => toggleType(t)}
											className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-200"
										>
											<BsX className="w-3 h-3" />
										</button>
									</span>
								);
							})}
							{channelFilters.map((id) => {
								const ch = activeChannels.find((c) => c.id === id);
								if (!ch) return null;
								return (
									<span
										key={id}
										className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-100 text-primary-700 border border-primary-200 rounded-full pl-2.5 pr-1 py-0.5"
									>
										{ch.displayName}
										<button
											type="button"
											onClick={() => toggleChannel(id)}
											className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-200"
										>
											<BsX className="w-3 h-3" />
										</button>
									</span>
								);
							})}
							<button
								type="button"
								onClick={clearFilters}
								className="text-[11px] text-bgBase-800 hover:text-text ml-1"
							>
								Alle zurücksetzen
							</button>
							<span className="text-[11px] text-bgBase-800 ml-auto">{filtered.length} Ergebnisse</span>
						</div>
					)}

					<Card>
						<CrmCalendar
							ref={calendarRef}
							activities={filtered}
							initialView="timeGridWeek"
							onEventClick={handleEventClick}
							onDateClick={handleDateClick}
							onDatesSet={handleDatesSet}
							headerToolbar={false}
							height={640}
						/>
					</Card>
				</div>

				{openActivity && (
					<ActivityDrawer
						activity={openActivity}
						members={members}
						onClose={() => setOpenId(null)}
						onComplete={handleComplete}
						onCancelAutomated={(a) =>
							updateMutation.mutateAsync({ id: a.id, payload: { status: 'cancelled' } })
						}
						onDelete={handleDelete}
						onReassign={handleReassign}
						onUpdate={(id, patch) => updateMutation.mutateAsync({ id, payload: patch })}
						navigate={navigate}
					/>
				)}
			</div>

			{meetingModal.open && (
				<MeetingScheduleModal
					isOpen
					onClose={() => setMeetingModal({ open: false, prefillDate: null })}
					prefillDate={meetingModal.prefillDate}
				/>
			)}

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={confirmDelete}
				title={`Aktivität "${deleteTarget?.title ?? ''}" löschen`}
				message="Möchten Sie diese Aktivität wirklich löschen?"
				confirmText="Löschen"
				isLoading={deleteMutation.isPending}
			/>
		</Page>
	);
};

export default CrmCalendarPage;
